import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Camera, Phone, Mail, MapPin, Briefcase, Building2, Check, User, CreditCard, CircleAlert, GraduationCap, FileText, UserPlus, FileCheck, FileSignature, ChevronDown, ChevronUp, ChevronRight, ChevronLeft, Save, Send, Loader2, FileDown, X } from 'lucide-react';
import { TextField, MenuItem, Checkbox, Tooltip, InputAdornment } from '@mui/material';
import TailwindModal from './TailwindModal';
import { DEGREES, PG_DEGREES } from '../utils/constants';

const CustomFileInput = ({ name, onChange, required, hasError, accept, value, className }) => {
    const [fileName, setFileName] = React.useState('');

    React.useEffect(() => {
        if (value instanceof File) {
            setFileName(value.name);
        } else if (value && typeof value === 'object' && value.name) {
            setFileName(value.name);
        } else if (typeof value === 'string' && value) {
            const parts = value.split(/[/\\]/);
            setFileName(parts[parts.length - 1] || 'Uploaded File');
        } else {
            setFileName('');
        }
    }, [value]);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setFileName(file.name);
        } else if (!value) {
            setFileName('');
        }
        if (onChange) onChange(e);
    };

    const handleClear = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setFileName('');
        if (onChange) onChange({ target: { name, files: [] } });
    };

    return (
        <div className={`relative w-full ${className || ''}`}>
            <input 
                type="file" 
                name={name}
                accept={accept}
                onChange={handleFileChange}
                required={required && !value}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                title={fileName || "Choose a file"}
            />
            <div className={`w-full flex items-center px-2 py-1 border ${hasError ? 'border-red-500 ring-2 ring-red-500/20' : 'border-slate-300 hover:border-slate-400'} rounded-lg bg-white transition-all shadow-sm h-[42px]`}>
                <div className="flex-shrink-0 flex items-center justify-center px-3 py-1.5 bg-blue-50 text-blue-700 text-sm font-medium rounded-md hover:bg-blue-100 transition-colors mr-3">
                    Browse
                </div>
                <span className={`text-sm truncate flex-1 ${fileName ? 'text-slate-800' : 'text-slate-400'}`}>
                    {fileName || 'Choose a file...'}
                </span>
                {fileName && (
                    <button type="button" onClick={handleClear} className="relative z-20 flex-shrink-0 w-6 h-6 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors ml-2">
                        <X size={14} />
                    </button>
                )}
            </div>
        </div>
    );
};

const SharedOnboardingForm = ({
    isUS = false,
    isReadOnly = false,
    type,
    state,
    handleChange,
    handleImageChange,
    handlePdfChange,
    emptyFields = [],
    formError = { general: "" },
    ImgError = "",
    PdfError = {},
    ug = [],
    handleUgFormChange,
    addUgForm,
    deleteUgForm,
    pg = [],
    handlePgFormChange,
    addPgForm,
    deletePgForm,
    work = [],
    handleWorkFormChange,
    addWorkForm,
    deleteWorkForm,
    workDate = [],
    displayWork = false,
    diaplayPassport = "no",
    checked,
    handleCheckBoxChange,
    modalOpen,
    handleSubmit,
    dynamicCustomFields = [],
    dynamicCustomValues = {},
    dynamicCustomFiles = {},
    handleDynamicFieldChange,
    dynamicRequiredDocs = [],
    dynamicDocFiles = {},
    handleDynamicDocChange,
    dynamicDocErrors = {},
    downloadFile,
    showFile,
    modal,
    setModal,
    loading = false
}) => {
    const [activeAccordion, setActiveAccordion] = useState('personal');

    useEffect(() => {
        if (loading) setActiveAccordion(null);
    }, [loading]);

    const toggleAccordion = (id) => {
        setActiveAccordion(prev => prev === id ? null : id);
    };

    const fieldToStepMap = {
        'Salutation': 'personal', 'Full_Name': 'personal', 'Gender': 'personal', 'Nationality': 'personal', 'FathersName': 'personal', 
        'MaritalStatus': 'personal', 'SpouseName': 'personal', 'BloodGroup': 'personal', 'PhysicallyChallenged': 'personal', 
        'DateOfBirth': 'personal', 'address': 'personal', 'PersonalEmail': 'contact', 'MobileNumber': 'contact', 
        'EmergencyContact': 'contact', 'RelationshipOfEmergency': 'contact', 'pincode': 'personal',
        
        'PAN_Number': 'professional', 'AADHAAR_Number': 'professional', 'NamePerAadhaar': 'professional', 'GenderPerAadhaar': 'professional', 
        'Date_of_Birth_as_per_Aadhaar': 'professional', 'UAN': 'professional', 'PFNo': 'professional', 'FreshEmployementForPf': 'professional', 
        'SSN': 'professional', 'PassportNumber': 'professional', 'Pan_Card_Upload': 'professional', 'Aadhaar_Card_Upload': 'professional', 
        'Upload_Passport_Size_photo_Copy': 'personal', 'Resume_Upload': 'professional', 'ESIC_Card_Upload': 'professional',
        'ciia': 'professional', 'holdPassport': 'professional',
        
        'Tenth_Marksheet_Upload': 'education', 'Twelth_Marksheet_Upload': 'education', 'UG_Provisional_Or_Degree_Certificate': 'education', 
        'PG_Provisional_Or_Degree_Certificate': 'education', 'RollNo': 'education', 'TenthPassed': 'education', 'TwelthPassed': 'education',
        
        'Bank_Name': 'bank', 'BankBranch': 'bank', 'IFCCode': 'bank', 'BankAccountNumber': 'bank', 'NameInBank': 'bank'
    };

    const getStepIdForField = (fieldName) => {
        if (fieldToStepMap[fieldName]) return fieldToStepMap[fieldName];
        if (fieldName.startsWith('ug') || fieldName.startsWith('pg') || fieldName.startsWith('Pg') || fieldName.includes('UG_') || fieldName.includes('PG_') || fieldName.startsWith('Degree') || fieldName.startsWith('department') || fieldName.startsWith('collegeName')) return 'education';
        if (fieldName.startsWith('Designation') || fieldName.startsWith('From') || fieldName.startsWith('To') || fieldName.startsWith('Experience_Certificate')) return 'professional';
        if (fieldName.startsWith('Bank') || fieldName.startsWith('IFC')) return 'bank';
        return 'review'; 
    };

    const [prevGeneralError, setPrevGeneralError] = React.useState("");

    React.useEffect(() => {
        if (formError.general && formError.general !== prevGeneralError) {
            if (emptyFields && emptyFields.length > 0) {
                const firstErrorField = emptyFields[0];
                const stepId = getStepIdForField(firstErrorField);
                
                setActiveAccordion(stepId);

                setTimeout(() => {
                    const accordionElement = document.getElementById(`accordion-${stepId}`);
                    if (accordionElement) {
                        accordionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);
            }
        }
        setPrevGeneralError(formError.general || "");
    }, [emptyFields, formError.general, prevGeneralError]);

    // calculate statuses
    const personalDone = !!(state?.BloodGroup && state?.Salutation && state?.Full_Name && state?.Gender && state?.Nationality && state?.FathersName && state?.MaritalStatus && state?.DateOfBirth && state?.address && state?.pincode && state?.Upload_Passport_Size_photo_Copy);
    const contactDone = !!(state?.PersonalEmail && state?.MobileNumber && state?.EmergencyContact && state?.RelationshipOfEmergency);
    const bankDone = !!(state?.Bank_Name && state?.BankBranch && state?.BankAccountNumber && state?.IFCCode && state?.NameInBank);
    const profDone = isUS ? 
        !!(state?.FreshEmployementForPf && state?.SSN && state?.holdPassport && (state?.holdPassport === 'yes' || state?.holdPassport === 'Yes' ? state?.PassportNumber : true)) : 
        !!(state?.FreshEmployementForPf && state?.PAN_Number && state?.AADHAAR_Number && state?.NamePerAadhaar && state?.GenderPerAadhaar && state?.Date_of_Birth_as_per_Aadhaar && state?.Pan_Card_Upload && state?.Aadhaar_Card_Upload && (type === 'Employee' && state?.FreshEmployementForPf !== 'Yes' ? state?.PFNo : true));
    const eduDone = isUS ? 
        !!(ug?.length > 0 && ug[0]?.Degree) : 
        !!(state?.TenthPassed && state?.TwelthPassed && state?.Tenth_Marksheet_Upload && state?.Twelth_Marksheet_Upload && (type === 'Intern' ? state?.RollNo : true) && ug?.length > 0 && ug[0]?.Degree);

    const getStatus = (id, condition) => {
        if (condition) return 'Completed';
        if (activeAccordion === id) return 'In Progress';
        return 'Pending';
    };

    const steps = [
        { id: 'personal', label: 'Personal', status: getStatus('personal', personalDone) },
        { id: 'contact', label: 'Contact', status: getStatus('contact', contactDone) },
        { id: 'bank', label: 'Bank Details', status: getStatus('bank', bankDone) },
        { id: 'professional', label: 'Professional', status: getStatus('professional', profDone) },
        { id: 'education', label: 'Education', status: getStatus('education', eduDone) }
    ];

    const totalSteps = 5;
    const completedSteps = [personalDone, contactDone, bankDone, profDone, eduDone].filter(Boolean).length;
    const progressPercent = Math.round((completedSteps / totalSteps) * 100);

    const muiInputSx = { 
        '& .MuiOutlinedInput-root': { 
            height: '42px', 
            borderRadius: '8px', 
            fontSize: '14px', 
            backgroundColor: isReadOnly ? '#f8fafc' : '#ffffff',
            transition: 'all 0.2s',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#cbd5e1' },
            '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '2px', boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)' }
        }, 
        '& input': { padding: '0 14px', height: '42px', boxSizing: 'border-box', cursor: isReadOnly ? 'not-allowed' : 'text', color: '#1e293b' } 
    };
    
    const muiSelectSx = { 
        '& .MuiOutlinedInput-root': { 
            height: '42px', 
            borderRadius: '8px',
            backgroundColor: isReadOnly ? '#f8fafc' : '#ffffff',
            transition: 'all 0.2s',
            '& fieldset': { borderColor: '#e2e8f0' },
            '&:hover fieldset': { borderColor: '#cbd5e1' },
            '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: '2px', boxShadow: '0 0 0 4px rgba(59, 130, 246, 0.1)' }
        }, 
        '& .MuiSelect-select': { paddingTop: '0', paddingBottom: '0', height: '42px', display: 'flex', alignItems: 'center', fontSize: '14px', cursor: isReadOnly ? 'not-allowed' : 'pointer', color: '#1e293b' } 
    };
    
    const muiSelectProps = { displayEmpty: true, MenuProps: { sx: { '& .MuiMenuItem-root': { fontSize: '14px', minHeight: '36px' } } } };
    
    const fileInputClass = (hasErr) => `w-full text-sm text-slate-500 file:mr-3 file:py-[5px] file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border ${hasErr ? 'border-red-500 ring-2 ring-red-500/20' : 'border-slate-300 hover:border-slate-400'} rounded-lg bg-white box-border transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm`;

    const renderInput = (label, name, value, inputType = "text", required = false, isSelect = false, options = [], error = null) => {
        const hasError = emptyFields.includes(name) || !!error;
        const isDisabled = isReadOnly;

        if (isSelect) {
            return (
                <div className={`w-full ${isDisabled ? 'cursor-not-allowed' : ''}`}>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
                    <TextField 
                        error={hasError}
                        select 
                        size="small" 
                        name={name} 
                        value={value || ""} 
                        onChange={handleChange} 
                        required={required} 
                        disabled={isDisabled}
                        className={`w-full bg-white ${hasError ? '[&_fieldset]:!border-red-500' : ''} ${isDisabled ? 'pointer-events-none' : ''}`} 
                        sx={muiSelectSx}
                        SelectProps={muiSelectProps}
                    >
                        <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5, color: '#94a3b8' }}>Select {label}</MenuItem>
                        {options.map((opt, i) => (
                            <MenuItem key={i} value={opt.value} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>{opt.label}</MenuItem>
                        ))}
                    </TextField>
                    {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
                </div>
            );
        }

        if (inputType === 'file') {
            return (
                <div className={`w-full ${isDisabled ? 'cursor-not-allowed' : ''}`}>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
                    {isReadOnly ? (
                        value ? (
                            <div className="flex items-center gap-2 w-full mt-1">
                                <button 
                                    type="button" 
                                    onClick={() => showFile && showFile(value)}
                                    className="flex-1 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]"
                                >
                                    <FileText size={16} /> View
                                </button>
                                <button 
                                    type="button" 
                                    onClick={() => downloadFile && downloadFile(value)}
                                    className="flex-1 px-3 py-2 bg-blue-600 border border-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 hover:border-blue-700 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]"
                                >
                                    <FileDown size={16} /> Download
                                </button>
                            </div>
                        ) : (
                            <div className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-400 bg-slate-50 mt-1 flex items-center h-[42px]">No file attached</div>
                        )
                    ) : (
                        <>
                            <CustomFileInput 
                                name={name}
                                onChange={name === 'Upload_Passport_Size_photo_Copy' ? handleImageChange : handlePdfChange}
                                required={required}
                                hasError={hasError}
                                value={value}
                                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png"
                            />
                            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
                        </>
                    )}
                </div>
            );
        }
        
        return (
            <div className={`w-full ${isDisabled ? 'cursor-not-allowed' : ''}`}>
                <label className="block text-xs font-semibold text-slate-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
                <TextField 
                    error={hasError}
                    type={inputType}
                    size="small" 
                    name={name} 
                    value={value || ""} 
                    onChange={handleChange} 
                    required={required} 
                    disabled={isDisabled}
                    placeholder={`Enter ${label}`}
                    className={`w-full bg-white ${hasError ? '[&_fieldset]:!border-red-500' : ''} ${isDisabled ? 'pointer-events-none' : ''}`} 
                    sx={muiInputSx}
                />
                {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
            </div>
        );
    };

    const renderAccordion = (id, icon, title, subtitle, status, content, prevId, nextId) => {
        const isExpanded = isReadOnly || activeAccordion === id;
        return (
            <div id={`accordion-${id}`} className={`border rounded-xl mb-4 overflow-hidden transition-all duration-300 ${(isExpanded && !isReadOnly) ? 'border-blue-500 shadow-md bg-white' : 'border-slate-200 bg-white hover:border-slate-300 shadow-sm'}`}>
                {/* Header */}
                <div 
                    className={`p-4 sm:p-5 flex items-center justify-between ${(loading || isReadOnly) ? (isReadOnly ? 'cursor-default' : 'cursor-not-allowed opacity-75') : 'cursor-pointer'}`}
                    onClick={() => { if (!loading && !isReadOnly) toggleAccordion(id); }}
                >
                    <div className="flex items-center gap-4">
                        <div className={`p-3 rounded-xl flex items-center justify-center ${
                            status === 'Completed' ? 'bg-green-50 text-green-600' :
                            status === 'In Progress' ? 'bg-blue-50 text-blue-600' :
                            'bg-slate-50 text-slate-500'
                        }`}>
                            {icon}
                        </div>
                        <div>
                            <h3 className="font-bold text-slate-800 text-sm sm:text-base m-0">{title}</h3>
                            <p className="text-xs sm:text-sm text-slate-500 m-0 mt-0.5">{subtitle}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div className={`px-2 py-1 sm:px-3 sm:py-1 text-[10px] sm:text-xs font-semibold rounded-full flex items-center gap-1.5 ${
                            status === 'Completed' ? 'bg-green-100 text-green-700' :
                            status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                            'bg-slate-100 text-slate-600'
                        }`}>
                            {status === 'Completed' && <Check size={12} />}
                            {status}
                        </div>
                        <div className={`transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}>
                            {!isReadOnly && <ChevronDown size={20} className="text-slate-400" />}
                        </div>
                    </div>
                </div>
                {/* Content */}
                {isExpanded && (
                    <div className="p-4 sm:p-5 pt-0 border-t border-slate-100 mt-2 bg-slate-50/50">
                        {content}
                        {!isReadOnly && (
                            <div className="mt-6 flex justify-between items-center border-t border-slate-200/60 pt-4">
                                <div>
                                    {prevId && (
                                        <button 
                                            type="button" 
                                            onClick={() => {
                                                toggleAccordion(prevId);
                                                setTimeout(() => {
                                                    const el = document.getElementById(`accordion-${prevId}`);
                                                    if (el) {
                                                        const y = el.getBoundingClientRect().top + window.scrollY - 100;
                                                        window.scrollTo({ top: y, behavior: 'smooth' });
                                                    }
                                                }, 150);
                                            }} 
                                            className="px-5 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold rounded-lg shadow-sm transition-all flex items-center gap-2"
                                        >
                                            <ChevronLeft size={16} strokeWidth={2.5} /> Previous
                                        </button>
                                    )}
                                </div>
                                <div>
                                    {nextId && (
                                        <button 
                                            type="button" 
                                            onClick={() => {
                                                toggleAccordion(nextId);
                                                setTimeout(() => {
                                                    const el = document.getElementById(`accordion-${nextId}`);
                                                    if (el) {
                                                        const y = el.getBoundingClientRect().top + window.scrollY - 100;
                                                        window.scrollTo({ top: y, behavior: 'smooth' });
                                                    }
                                                }, 150);
                                            }} 
                                            className="px-5 py-2.5 text-sm font-semibold rounded-lg shadow-sm transition-all flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white hover:shadow"
                                        >
                                            Next <ChevronRight size={16} strokeWidth={2.5} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        )
    };

    return (
<div className={`w-full flex-1 flex flex-col gap-6 ${isReadOnly ? '' : 'max-w-6xl mx-auto'} px-4 md:px-6 lg:px-8 pb-4 md:pb-6 lg:pb-8 pt-0`}>
            {/* Render Progress Tracker into the sticky header portal if it exists and not in HR readonly mode */}
            {document.getElementById('onboarding-progress-portal') && !isReadOnly && createPortal(
                <div className="w-full sm:w-64 bg-slate-50/50 p-2 sm:p-2.5 rounded-xl border border-slate-200">
                    <div className="flex justify-between items-center mb-1.5">
                        <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">Progress</span>
                        <span className="text-[10px] font-bold text-blue-600">{progressPercent}%</span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-1.5 mb-1.5">
                        <div className="bg-blue-600 h-1.5 rounded-full transition-all duration-500" style={{ width: `${progressPercent}%` }}></div>
                    </div>
                    <p className="text-[9px] text-slate-400 font-medium m-0 text-right">Step {progressPercent === 100 ? 5 : steps.findIndex(s => s.id === activeAccordion) + 1} of 5</p>
                </div>,
                document.getElementById('onboarding-progress-portal')
            )}

            {/* Horizontal Stepper */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm overflow-x-auto">
                <div className="flex items-center justify-between min-w-[600px] px-2">
                    {steps.map((step, idx) => (
                        <React.Fragment key={step.id}>
                            <div className={`flex flex-col items-center gap-2 ${loading ? 'cursor-not-allowed opacity-75' : 'cursor-pointer'}`} onClick={() => { if (!loading) toggleAccordion(step.id); }}>
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors z-10 ${
                                    step.status === 'Completed' ? 'bg-white border-blue-600 text-blue-600' :
                                    activeAccordion === step.id ? 'bg-blue-600 border-blue-600 text-white' :
                                    'bg-slate-50 border-slate-200 text-slate-400'
                                }`}>
                                    {step.status === 'Completed' ? <Check size={16} /> : (idx + 1)}
                                </div>
                                <div className="text-center">
                                    <p className={`text-xs font-bold m-0 ${activeAccordion === step.id || step.status === 'Completed' ? 'text-slate-800' : 'text-slate-500'}`}>{step.label}</p>
                                    <p className={`text-[10px] m-0 mt-0.5 ${step.status === 'Completed' ? 'text-blue-600' : 'text-slate-400'}`}>{step.status}</p>
                                </div>
                            </div>
                            {idx < steps.length - 1 && (
                                <div className={`flex-1 h-[2px] mx-2 -mt-8 ${
                                    step.status === 'Completed' ? 'bg-blue-200' : 'bg-slate-100'
                                }`}></div>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            </div>

            {/* Accordions */}
            <div className="w-full">
                {renderAccordion('personal', <User size={24} />, "Personal Information", "Tell us about yourself", steps[0].status, (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {renderInput("Salutation", "Salutation", state?.Salutation, "text", true, true, [
                            {label: 'Mr', value: 'Mr'}, {label: 'Ms', value: 'Ms'}, {label: 'Mrs', value: 'Mrs'}, {label: 'Dr', value: 'Dr'}
                        ])}
                        {renderInput("Full Name", "Full_Name", state?.Full_Name, "text", true)}
                        {renderInput("Gender", "Gender", state?.Gender, "text", true, true, [
                            {label: 'Male', value: 'Male'}, {label: 'Female', value: 'Female'}
                        ])}
                        {renderInput("Nationality", "Nationality", state?.Nationality, "text", true, true, [
                            {label: 'India', value: 'India'}, {label: 'US', value: 'US'}, {label: 'Others', value: 'Others'}
                        ])}
                        {renderInput("Father's Name", "FathersName", state?.FathersName, "text", true)}
                        {renderInput("Marital Status", "MaritalStatus", state?.MaritalStatus, "text", true, true, [
                            {label: 'Married', value: 'Married'}, {label: 'Single', value: 'Single'}
                        ])}
                        {renderInput("Spouse Name", "SpouseName", state?.SpouseName, "text")}
                        {renderInput("Blood Group", "BloodGroup", state?.BloodGroup, "text", true, true, [
                            {label: 'A+', value: 'A+'}, {label: 'A-', value: 'A-'}, {label: 'B+', value: 'B+'}, {label: 'B-', value: 'B-'},
                            {label: 'AB+', value: 'AB+'}, {label: 'AB-', value: 'AB-'}, {label: 'O+', value: 'O+'}, {label: 'O-', value: 'O-'},
                            {label: "I Don't Know", value: "I Don't Know"}
                        ])}
                        {renderInput("Physically Challenged", "PhysicallyChallenged", state?.PhysicallyChallenged, "text", true, true, [
                            {label: 'Yes', value: 'Yes'}, {label: 'No', value: 'No'}
                        ])}
                        {renderInput("Date Of Birth", "DateOfBirth", state?.DateOfBirth, "date", true)}
                        {renderInput("Address", "address", state?.address, "text", true)}
                        {renderInput("Pincode", "pincode", state?.pincode, "number", true)}
                        
                        {renderInput("Upload Passport Size Photo", "Upload_Passport_Size_photo_Copy", state?.Upload_Passport_Size_photo_Copy, "file", !isReadOnly, false, [], ImgError)}

                    </div>
                ), null, 'contact')}

                {renderAccordion('contact', <Phone size={24} />, "Contact Information", "How can we reach you?", steps[1].status, (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                        {renderInput("Mobile Number", "MobileNumber", state?.MobileNumber, "text", true)}
                        {renderInput("Personal Email Address", "PersonalEmail", state?.PersonalEmail, "text", true)}
                        {renderInput("Emergency Contact", "EmergencyContact", state?.EmergencyContact, "text", true)}
                        {renderInput("Relationship of Emergency", "RelationshipOfEmergency", state?.RelationshipOfEmergency, "text", true)}
                    </div>
                ), 'personal', 'bank')}

                {renderAccordion('bank', <CreditCard size={24} />, "Bank Details", "Your bank account information", steps[2].status, (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                        {renderInput("Bank Name", "Bank_Name", state?.Bank_Name, "text", true)}
                        {renderInput("Bank Branch", "BankBranch", state?.BankBranch, "text", true)}
                        {renderInput("IFSC Code", "IFCCode", state?.IFCCode, "text", true)}
                        {renderInput("Bank Account Number", "BankAccountNumber", state?.BankAccountNumber, "text", true)}
                        {renderInput("Name in Bank Account", "NameInBank", state?.NameInBank, "text", true)}
                    </div>
                ), 'contact', 'professional')}

                {renderAccordion('professional', <Briefcase size={24} />, "Professional Information", "Your professional background", steps[3].status, (
                    <div className="flex flex-col gap-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {isUS ? (
                                <>
                                    {renderInput("Fresh Employment", "FreshEmployementForPf", state?.FreshEmployementForPf, "text", true, true, [
                                        {label: 'Yes', value: 'Yes'}, {label: 'No', value: 'No'}
                                    ])}
                                    {renderInput("SSN", "SSN", state?.SSN, "text", true)}
                                    {renderInput("Do you hold Passport", "holdPassport", state?.holdPassport, "text", true, true, [
                                        {label: 'Yes', value: 'Yes'}, {label: 'No', value: 'No'}
                                    ])}
                                    {diaplayPassport === "yes" && renderInput("Passport Number", "PassportNumber", state?.PassportNumber, "text", true)}
                                </>
                            ) : (
                                <>
                                    {renderInput("Fresh Employment for PF", "FreshEmployementForPf", state?.FreshEmployementForPf, "text", type === 'Employee', true, [
                                        {label: 'Yes', value: 'Yes'}, {label: 'No', value: 'No'}
                                    ])}
                                    {state?.FreshEmployementForPf === 'No' && (
                                        <>
                                            {renderInput("PF No", "PFNo", state?.PFNo, "text", type === 'Employee')}
                                            {renderInput("UAN Number", "UAN", state?.UAN, "text")}
                                        </>
                                    )}
                                    {renderInput("Name as per Aadhaar", "NamePerAadhaar", state?.NamePerAadhaar, "text", true)}
                                    {renderInput("AADHAAR Number", "AADHAAR_Number", state?.AADHAAR_Number, "text", true)}
                                    {renderInput("Gender as per Aadhaar", "GenderPerAadhaar", state?.GenderPerAadhaar, "text", true, true, [
                                        {label: 'Male', value: 'Male'}, {label: 'Female', value: 'Female'}
                                    ])}
                                    {renderInput("Date of Birth as per Aadhaar", "Date_of_Birth_as_per_Aadhaar", state?.Date_of_Birth_as_per_Aadhaar, "date", true)}
                                    
                                    {renderInput("Aadhaar Card Upload (Front and Back)", "Aadhaar_Card_Upload", state?.Aadhaar_Card_Upload, "file", !isReadOnly, false, [], PdfError?.Aadhaar_Card_Upload)}
                                    {renderInput("PAN Number", "PAN_Number", state?.PAN_Number, "text", true)}
                                    {renderInput("Pan Card Upload", "Pan_Card_Upload", state?.Pan_Card_Upload, "file", !isReadOnly, false, [], PdfError?.Pan_Card_Upload)}
                                </>
                            )}
                            
                            {/* Render Dynamic Required Documents */}
                            {dynamicRequiredDocs?.length > 0 && dynamicRequiredDocs.map((doc, index) => (
                                <div key={doc.id || doc.fieldName || index} className={`w-full ${isReadOnly ? 'cursor-not-allowed' : ''}`}>
                                    <div className="flex justify-between items-center mb-1">
                                        <label className="block text-xs font-semibold text-slate-700">
                                            <span>{doc.fieldName} <span className="text-red-500">*</span></span>
                                        </label>
                                    {!isReadOnly && doc.linkedFile && (
                                        <div className="flex gap-3">
                                            <button
                                                type="button"
                                                onClick={() => downloadFile?.(doc.linkedFile)}
                                                className="flex items-center gap-1 text-xs font-semibold text-blue-600 hover:underline max-w-[220px]"
                                                title={doc.linkedFile}
                                            >
                                                <FileDown size={14} className="shrink-0" />
                                                <span className="truncate overflow-hidden whitespace-nowrap">
                                                    {typeof doc.linkedFile === "object"
                                                        ? doc.linkedFile.name
                                                        : doc.linkedFile.split("/").pop()}
                                                </span>
                                            </button>
                                        </div>
                                    )}
                                    </div>
                                    {isReadOnly ? (
                                        doc.linkedFile ? (
                                            <div className="flex gap-2 mt-1">
                                                <button type="button" onClick={() => showFile && showFile(doc.linkedFile)} 
                                                className="flex-1 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]">
                                                    <FileText size={16} /> View</button>
                                                <button type="button" onClick={() => downloadFile && downloadFile(doc.linkedFile)} 
                                                className="flex-1 px-3 py-2 bg-blue-600 border border-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 hover:border-blue-700 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]">
                                                    <FileDown size={16} /> Download</button>
                                            </div>
                                        ) : (
                                            <div className="px-3 py-2 border border-slate-200 rounded-lg text-xs text-slate-400 bg-white mt-1 flex items-center h-[42px]">No file</div>
                                        )
                                    ) : (
                                        <>
                                            <CustomFileInput 
                                                name={`dynamic_doc_${doc.fieldName}`}
                                                onChange={(e) => handleDynamicDocChange(e, doc.fieldName, false, doc.linkedFile)}
                                                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png"
                                                required={true}
                                                hasError={emptyFields.includes(`dynamic_doc_${doc.fieldName}`)}
                                                value={dynamicDocFiles[doc.fieldName] || dynamicDocFiles[`dynamic_doc_${doc.fieldName}`]}
                                            />
                                            {dynamicDocErrors && dynamicDocErrors[`dynamic_doc_${doc.fieldName}`] && (
                                                <p className="text-red-500 text-xs mt-1">{dynamicDocErrors[`dynamic_doc_${doc.fieldName}`]}</p>
                                            )}
                                        </>
                                    )}
                                </div>
                            ))}

                            {/* Render Dynamic Custom Fields */}
                            {dynamicCustomFields?.length > 0 && dynamicCustomFields.map((field, idx) => (
                                <div key={field.id || field.fieldName || idx} className={`w-full ${isReadOnly ? 'cursor-not-allowed' : ''}`}>
                                    <label className="block text-xs font-semibold text-slate-700 mb-1">
                                        {field.fieldName} {field.required && <span className="text-red-500">*</span>}
                                    </label>
                                    {field.type === 'File' ? (
                                        isReadOnly ? (
                                            dynamicCustomValues[`dynamic_custom_file_${field.fieldName}`] ? (
                                                <div className="flex gap-2 mt-2 w-full">
                                                    <button 
                                                        type="button" 
                                                        onClick={() => showFile && showFile(dynamicCustomValues[`dynamic_custom_file_${field.fieldName}`])}
                                                        className="flex-1 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]"
                                                    >
                                                        <FileText size={16} /> View
                                                    </button>
                                                    <button 
                                                        type="button" 
                                                        onClick={() => downloadFile && downloadFile(dynamicCustomValues[`dynamic_custom_file_${field.fieldName}`])}
                                                        className="flex-1 px-3 py-2 bg-blue-600 border border-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 hover:border-blue-700 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]"
                                                    >
                                                        <FileDown size={16} /> Download
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-400 bg-slate-50 mt-1 flex items-center h-[42px]">No file attached</div>
                                            )
                                        ) : (
                                            <>
                                                <CustomFileInput 
                                                    name={`dynamic_custom_file_${field.fieldName}`}
                                                    onChange={(e) => handleDynamicDocChange(e, field.fieldName, true)}
                                                    required={field.required}
                                                    accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png"
                                                    hasError={emptyFields.includes(`dynamic_custom_file_${field.fieldName}`)}
                                                    value={dynamicCustomFiles[`dynamic_custom_file_${field.fieldName}`]}
                                                />
                                                {dynamicDocErrors && dynamicDocErrors[`dynamic_custom_file_${field.fieldName}`] && (
                                                    <p className="text-red-500 text-xs mt-1">{dynamicDocErrors[`dynamic_custom_file_${field.fieldName}`]}</p>
                                                )}
                                            </>
                                        )
                                    ) : (
                                        <TextField 
                                            error={emptyFields.includes(`dynamic_field_${field.fieldName}`)}
                                            type={field.type === 'Number' ? 'number' : field.type === 'Date' ? 'date' : 'text'}
                                            size="small" 
                                            name={`dynamic_field_${field.fieldName}`}
                                            value={dynamicCustomValues[`dynamic_field_${field.fieldName}`] || ""} 
                                            onChange={(e) => handleDynamicFieldChange && handleDynamicFieldChange(e, field.fieldName)} 
                                            required={field.required} 
                                            disabled={isReadOnly}
                                            placeholder={`Enter ${field.fieldName}`}
                                            className={`w-full bg-white ${emptyFields.includes(`dynamic_field_${field.fieldName}`) ? '[&_fieldset]:!border-red-500' : ''} ${isReadOnly ? 'pointer-events-none' : ''}`} 
                                            sx={muiInputSx}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>

                        {displayWork && work?.length > 0 && (
                            <div className="border-t border-slate-100 pt-4">
                                <h4 className="text-sm font-bold text-slate-800 mb-4">Work Experience</h4>
                                {work.map((form, index) => (
                                    <div key={form.id || `work-${index}`} className="bg-slate-50 border border-slate-100 rounded-xl p-4 mb-4 relative">
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">Designation <span className="text-red-500">*</span></label>
                                                <TextField disabled={isReadOnly} error={emptyFields.includes(`Designation${index + 1}`)} className={`w-full bg-white ${isReadOnly ? 'pointer-events-none' : ''}`} type='text' name="Designation" value={form.Designation} onChange={(e) => handleWorkFormChange(index, e)} required sx={muiInputSx} />
                                            </div>
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">From <span className="text-red-500">*</span></label>
                                                <TextField disabled={isReadOnly} error={emptyFields.includes(`From${index + 1}`)} className={`w-full bg-white ${isReadOnly ? 'pointer-events-none' : ''}`} type='date' name="From" value={form.From} onChange={(e) => handleWorkFormChange(index, e)} required sx={muiInputSx} />
                                            </div>
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">To <span className="text-red-500">*</span></label>
                                                <TextField disabled={isReadOnly} error={emptyFields.includes(`To${index + 1}`)} className={`w-full bg-white ${isReadOnly ? 'pointer-events-none' : ''}`} type='date' name="To" value={form.To} onChange={(e) => handleWorkFormChange(index, e)} required sx={muiInputSx} inputProps={{ min: workDate?.[index]?.min }} />
                                            </div>
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">Experience Certificate <span className="text-red-500">*</span></label>
                                                {isReadOnly ? (
                                                    form?.file ? (
                                                        <div className="flex gap-2">
                                                            <button type="button" onClick={() => showFile && showFile(form?.file)} className="flex-1 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]"><FileText size={16} /> View</button>
                                                            <button type="button" onClick={() => downloadFile && downloadFile(form?.file)} className="flex-1 px-3 py-2 bg-blue-600 border border-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 hover:border-blue-700 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]"><FileDown size={16} /> Download</button>
                                                        </div>
                                                    ) : (
                                                        <div className="px-3 py-2 border border-slate-200 rounded-lg text-xs text-slate-400 bg-white flex items-center h-[42px]">No file</div>
                                                    )
                                                ) : (
                                                    <CustomFileInput 
                                                        name="Experience_Certificate" 
                                                        onChange={(e) => handleWorkFormChange(index, e)} 
                                                        required={true}
                                                        accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.jpg,.jpeg,.png"
                                                        hasError={emptyFields.includes(`Experience_Certificate${index + 1}`)}
                                                        value={form.Experience_Certificate}
                                                    />
                                                )}
                                                {PdfError["Experience_Certificate" + (index + 1)] && <p className="text-red-500 text-xs mt-1">{PdfError["Experience_Certificate" + (index + 1)]}</p>}
                                            </div>
                                        </div>
                                        {!isReadOnly && (
                                            <div className="absolute -top-3 -right-3 flex gap-2">
                                                {index === work.length - 1 && work.length < 5 && (
                                                    <button type="button" className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-md hover:bg-blue-700" onClick={addWorkForm}>+</button>
                                                )}
                                                <button type="button" className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center shadow-md hover:bg-red-200" onClick={() => deleteWorkForm(index)}>−</button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ), 'bank', 'education')}

                {renderAccordion('education', <GraduationCap size={24} />, "Educational Details", "Your educational background", steps[4].status, (
                    <div className="flex flex-col gap-6">
                        <div className="flex flex-col gap-4">
                            {type === "Intern" && (
                                <div className="w-full md:w-1/3">
                                    {renderInput("Enrollment Number In College", "RollNo", state?.RollNo, "text", true)}
                                </div>
                            )}
                            {!isUS && (
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                    {renderInput("10th Passed Year", "TenthPassed", state?.TenthPassed, "text", true, true, [...Array(26)].map((_, i) => ({label: `${new Date().getFullYear() - i}`, value: new Date().getFullYear() - i})))}
                                    {renderInput("10th Marksheet Upload", "Tenth_Marksheet_Upload", state?.Tenth_Marksheet_Upload, "file", !isReadOnly, false, [], PdfError?.Tenth_Marksheet_Upload)}
                                    {renderInput("12th/Diploma Passed Year", "TwelthPassed", state?.TwelthPassed, "text", true, true, [...Array(26)].map((_, i) => ({label: `${new Date().getFullYear() - i}`, value: new Date().getFullYear() - i})))}
                                    {renderInput("12th/Diploma Marksheet Upload", "Twelth_Marksheet_Upload", state?.Twelth_Marksheet_Upload, "file", !isReadOnly, false, [], PdfError?.Twelth_Marksheet_Upload)}
                                </div>
                            )}
                        </div>

                        {ug?.length > 0 && (
                            <div className="border-t border-slate-100 pt-4">
                                <h4 className="text-sm font-bold text-slate-800 mb-4">Undergraduate Details (UG)</h4>
                                {ug.map((form, index) => (
                                    <div key={form.id || `ug-${index}`} className="bg-slate-50 border border-slate-100 rounded-xl p-4 mb-4 relative">
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">Degree <span className="text-red-500">*</span></label>
                                                <TextField select disabled={isReadOnly} error={emptyFields.includes(`ugDegree${index + 1}`)} size="small" name="Degree" value={form.Degree || ""} onChange={(e) => handleUgFormChange(index, e)} required className={`w-full bg-white ${emptyFields.includes(`ugDegree${index + 1}`) ? '[&_fieldset]:!border-red-500' : ''} ${isReadOnly ? 'pointer-events-none' : ''}`} sx={muiSelectSx} SelectProps={muiSelectProps}>
                                                    <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5, color: '#94a3b8' }}>Select an option</MenuItem>
                                                    {DEGREES.map((deg, i) => (
                                                        <MenuItem key={i} value={deg.value} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>{deg.label}</MenuItem>
                                                    ))}
                                                </TextField>
                                            </div>
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">Department <span className="text-red-500">*</span></label>
                                                <TextField disabled={isReadOnly} error={emptyFields.includes(`ugdepartment${index + 1}`)} className={`w-full bg-white ${emptyFields.includes(`ugdepartment${index + 1}`) ? '[&_fieldset]:!border-red-500' : ''} ${isReadOnly ? 'pointer-events-none' : ''}`} type='text' name="department" value={form.department} onChange={(e) => handleUgFormChange(index, e)} required sx={muiInputSx} />
                                            </div>
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">College Name <span className="text-red-500">*</span></label>
                                                <TextField disabled={isReadOnly} error={emptyFields.includes(`ugcollegeName${index + 1}`)} className={`w-full bg-white ${emptyFields.includes(`ugcollegeName${index + 1}`) ? '[&_fieldset]:!border-red-500' : ''} ${isReadOnly ? 'pointer-events-none' : ''}`} type='text' name="collegeName" value={form.collegeName} onChange={(e) => handleUgFormChange(index, e)} required sx={muiInputSx} />
                                            </div>
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">Provisional/Degree <span className="text-red-500">*</span></label>
                                                {isReadOnly ? (
                                                      form?.file ? (
                                                          <div className="flex gap-2">
                                                              <button type="button" onClick={() => showFile && showFile(form?.file)} className="flex-1 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]"><FileText size={16} /> View</button>
                                                              <button type="button" onClick={() => downloadFile && downloadFile(form?.file)} className="flex-1 px-3 py-2 bg-blue-600 border border-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 hover:border-blue-700 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]"><FileDown size={16} /> Download</button>
                                                          </div>
                                                      ) : (
                                                        <div className="px-3 py-2 border border-slate-200 rounded-lg text-xs text-slate-400 bg-white flex items-center h-[42px]">No file</div>
                                                    )
                                                ) : (
                                                    <CustomFileInput 
                                                        name="UG_Provisional_Or_Degree_Certificate" 
                                                        accept=".pdf"
                                                        onChange={(e) => handleUgFormChange(index, e)} 
                                                        required={true}
                                                        hasError={emptyFields.includes(`ugUG_Provisional_Or_Degree_Certificate${index + 1}`)}
                                                        value={form.UG_Provisional_Or_Degree_Certificate}
                                                    />
                                                )}
                                                {PdfError["UG_Provisional_Or_Degree_Certificate" + (index + 1)] && <p className="text-red-500 text-xs mt-1">{PdfError["UG_Provisional_Or_Degree_Certificate" + (index + 1)]}</p>}
                                            </div>
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">Passed Year <span className="text-red-500">*</span></label>
                                                {isReadOnly ? (
                                                    <TextField disabled className="w-full bg-white pointer-events-none" type="text" value={form.ugPassed || form.Passed || ""} sx={muiInputSx} />
                                                ) : (
                                                    <TextField select error={emptyFields.includes(`ugugPassed${index + 1}`)} size="small" name="ugPassed" value={form.ugPassed || form.Passed || ""} onChange={(e) => handleUgFormChange(index, e)} required className={`w-full bg-white ${emptyFields.includes(`ugugPassed${index + 1}`) ? '[&_fieldset]:!border-red-500' : ''}`} sx={muiSelectSx} SelectProps={muiSelectProps}>
                                                        <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5, color: '#94a3b8' }}>Select Year</MenuItem>
                                                        {[...Array(26)].map((_, i) => (
                                                            <MenuItem key={`ugPassed_${new Date().getFullYear() - i}`} value={new Date().getFullYear() - i} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>{new Date().getFullYear() - i}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                            </div>
                                        </div>
                                        {!isReadOnly && (
                                            <div className="absolute -top-3 -right-3 flex gap-2">
                                                {index === ug.length - 1 && ug.length < 3 && (
                                                    <button type="button" className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-md hover:bg-blue-700" onClick={addUgForm}>+</button>
                                                )}
                                                <button type="button" className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center shadow-md hover:bg-red-200" onClick={() => deleteUgForm(index)}>−</button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {pg?.length > 0 && (
                            <div className="border-t border-slate-100 pt-4">
                                <h4 className="text-sm font-bold text-slate-800 mb-4">Postgraduate Details (PG)</h4>
                                {pg.map((form, index) => (
                                    <div key={form.id || `pg-${index}`} className="bg-slate-50 border border-slate-100 rounded-xl p-4 mb-4 relative">
                                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">Degree</label>
                                                <TextField select disabled={isReadOnly} error={emptyFields.includes(`pgDegree${index + 1}`)} size="small" name="Degree" value={form.Degree || ""} onChange={(e) => handlePgFormChange(index, e)} className={`w-full bg-white ${emptyFields.includes(`pgDegree${index + 1}`) ? '[&_fieldset]:!border-red-500' : ''} ${isReadOnly ? 'pointer-events-none' : ''}`} sx={muiSelectSx} SelectProps={muiSelectProps} InputProps={{ endAdornment: form.Degree && !isReadOnly && (<InputAdornment position="end" sx={{ position: 'absolute', right: 28 }}><button type="button" onPointerDown={(e) => { e.stopPropagation(); handlePgFormChange(index, { target: { name: 'Degree', value: '' } }) }} className="text-slate-400 hover:text-red-500 rounded-full p-0.5 transition-colors z-50"><X size={14}/></button></InputAdornment>) }}>
                                                    <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5, color: '#94a3b8' }}>Select an option</MenuItem>
                                                    {PG_DEGREES.map((deg, i) => (
                                                        <MenuItem key={i} value={deg.value} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>{deg.label}</MenuItem>
                                                    ))}
                                                </TextField>
                                            </div>
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">Department</label>
                                                <TextField disabled={isReadOnly} error={emptyFields.includes(`pgdepartment${index + 1}`)} className={`w-full bg-white ${emptyFields.includes(`pgdepartment${index + 1}`) ? '[&_fieldset]:!border-red-500' : ''} ${isReadOnly ? 'pointer-events-none' : ''}`} type='text' name="department" value={form.department} onChange={(e) => handlePgFormChange(index, e)} sx={muiInputSx} InputProps={{ endAdornment: form.department && !isReadOnly && (<InputAdornment position="end"><button type="button" onClick={() => handlePgFormChange(index, { target: { name: 'department', value: '' } })} className="text-slate-400 hover:text-red-500 rounded-full p-0.5 transition-colors"><X size={14}/></button></InputAdornment>) }} />
                                            </div>
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">College Name</label>
                                                <TextField disabled={isReadOnly} error={emptyFields.includes(`pgcollegeName${index + 1}`)} className={`w-full bg-white ${emptyFields.includes(`pgcollegeName${index + 1}`) ? '[&_fieldset]:!border-red-500' : ''} ${isReadOnly ? 'pointer-events-none' : ''}`} type='text' name="collegeName" value={form.collegeName} onChange={(e) => handlePgFormChange(index, e)} sx={muiInputSx} InputProps={{ endAdornment: form.collegeName && !isReadOnly && (<InputAdornment position="end"><button type="button" onClick={() => handlePgFormChange(index, { target: { name: 'collegeName', value: '' } })} className="text-slate-400 hover:text-red-500 rounded-full p-0.5 transition-colors"><X size={14}/></button></InputAdornment>) }} />
                                            </div>
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">Provisional/Degree</label>
                                                {isReadOnly ? (
                                                      form.PG_Provisional_Or_Degree_Certificate ? (
                                                          <div className="flex gap-2">
                                                              <button type="button" onClick={() => showFile && showFile(form.PG_Provisional_Or_Degree_Certificate)} className="flex-1 px-3 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]"><FileText size={16} /> View</button>
                                                              <button type="button" onClick={() => downloadFile && downloadFile(form.PG_Provisional_Or_Degree_Certificate)} className="flex-1 px-3 py-2 bg-blue-600 border border-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 hover:border-blue-700 transition-colors shadow-sm flex justify-center items-center gap-1.5 h-[42px]"><FileDown size={16} /> Download</button>
                                                          </div>
                                                      ) : (
                                                        <div className="px-3 py-2 border border-slate-200 rounded-lg text-xs text-slate-400 bg-white flex items-center h-[42px]">No file</div>
                                                    )
                                                ) : (
                                                    <CustomFileInput 
                                                        name="PG_Provisional_Or_Degree_Certificate" 
                                                        accept=".pdf"
                                                        onChange={(e) => handlePgFormChange(index, e)} 
                                                        required={false}
                                                        hasError={emptyFields.includes(`pgPG_Provisional_Or_Degree_Certificate${index + 1}`)}
                                                        value={form.PG_Provisional_Or_Degree_Certificate}
                                                    />
                                                )}
                                                {PdfError["PG_Provisional_Or_Degree_Certificate" + (index + 1)] && <p className="text-red-500 text-xs mt-1">{PdfError["PG_Provisional_Or_Degree_Certificate" + (index + 1)]}</p>}
                                            </div>
                                            <div className="w-full">
                                                <label className="block text-xs font-semibold text-slate-700 mb-1">Passed Year</label>
                                                {isReadOnly ? (
                                                    <TextField disabled className="w-full bg-white pointer-events-none" type="text" value={form.PgPassed || form.Passed || ""} sx={muiInputSx} />
                                                ) : (
                                                    <TextField select error={emptyFields.includes(`pgPgPassed${index + 1}`)} size="small" name="PgPassed" value={form.PgPassed || form.Passed || ""} onChange={(e) => handlePgFormChange(index, e)} className={`w-full bg-white ${emptyFields.includes(`pgPgPassed${index + 1}`) ? '[&_fieldset]:!border-red-500' : ''}`} sx={muiSelectSx} SelectProps={muiSelectProps} InputProps={{ endAdornment: (form.PgPassed || form.Passed) && !isReadOnly && (<InputAdornment position="end" sx={{ position: 'absolute', right: 28 }}><button type="button" onPointerDown={(e) => { e.stopPropagation(); handlePgFormChange(index, { target: { name: 'PgPassed', value: '' } }) }} className="text-slate-400 hover:text-red-500 rounded-full p-0.5 transition-colors z-50"><X size={14}/></button></InputAdornment>) }}>
                                                        <MenuItem value="" disabled hidden sx={{ fontSize: '14px', minHeight: '32px', py: 0.5, color: '#94a3b8' }}>Select Year</MenuItem>
                                                        {[...Array(26)].map((_, i) => (
                                                            <MenuItem key={`pgPassed_${new Date().getFullYear() - i}`} value={new Date().getFullYear() - i} sx={{ fontSize: '14px', minHeight: '32px', py: 0.5 }}>{new Date().getFullYear() - i}</MenuItem>
                                                        ))}
                                                    </TextField>
                                                )}
                                            </div>
                                        </div>
                                        {!isReadOnly && (
                                            <div className="absolute -top-3 -right-3 flex gap-2">
                                                {index === pg.length - 1 && pg.length < 3 && (
                                                    <button type="button" className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center shadow-md hover:bg-blue-700" onClick={addPgForm}>+</button>
                                                )}
                                                <button type="button" className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center shadow-md hover:bg-red-200" onClick={() => deletePgForm(index)}>−</button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                ), 'professional', null)}

            </div>


            {/* Bottom Bar */}
            {!isReadOnly && (
                <div className="mt-auto bg-white border border-slate-200 rounded-xl py-3 px-5 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] flex flex-col gap-3 sticky bottom-4 z-20">
                    {/* Row 1: Error Message */}
                    {formError?.general && (
                        <div className="flex items-center gap-2 text-red-600 bg-red-50 px-3 py-2 rounded-lg border border-red-100">
                            <CircleAlert size={18} />
                            <p className="text-sm font-semibold mb-0">{formError.general}</p>
                        </div>
                    )}
                    
                    {/* Row 2: Checkbox and Button */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex flex-col w-full sm:w-auto">
                            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer font-medium select-none">
                                <Checkbox 
                                    checked={checked}
                                    onChange={handleCheckBoxChange}
                                    onClick={modalOpen}
                                    sx={{ '&.Mui-checked': { color: '#2563eb' } }}
                                    size="small"
                                    style={{ padding: '4px' }}
                                />
                                I confirm that all the information provided is correct to the best of my knowledge.
                            </label>
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <button 
                                type="button" 
                                disabled={!checked || loading}
                                onClick={handleSubmit}
                                className={`px-5 py-2 text-sm bg-blue-600 border-2 border-blue-600 text-white rounded-lg font-semibold transition-colors shadow-sm w-full sm:w-auto flex items-center justify-center ${(!checked || loading) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 hover:border-blue-700'}`}
                            >
                                {loading ? (
                                    <>Submitting Onboarding <Loader2 size={16} className="ml-2 animate-spin" /></>
                                ) : (
                                    <>Submit Onboarding <Send size={16} className="ml-2" /></>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {modal && setModal && (
                <TailwindModal isOpen={modal} toggle={() => setModal(false)}>
                    <div className="p-6">
                        <div className="text-center">
                            <p className="text-slate-700 mb-6 font-medium">
                                Profile details can be updated only once.<br /> Make sure that you update correct information
                            </p>
                            <button className="px-6 py-2 text-white rounded-lg bg-blue-600 hover:bg-blue-700 transition-colors font-medium shadow-sm" onClick={() => setModal(false)}>
                                OK
                            </button>
                        </div>
                    </div>
                </TailwindModal>
            )}
        </div>
    );
};

export default SharedOnboardingForm;
